import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import type { Schedule, ScheduleInput, ScheduleOccurrence } from "../../shared/contracts";
import { addDays, isHhMm, isIsoDate } from "../../shared/time";
import type { Person, Site } from "../../shared/types";
import { Dialog, Empty, Field } from "../components/ui";
import { del, patch, post } from "../lib/api";
import { useApi } from "../lib/useApi";
import {
  Check,
  ConfirmDialog,
  fmtIsoDate,
  LoadState,
  OccurrenceBadge,
  PageHeader,
  thisWeek,
  toApiError,
  usePeople,
  useSites,
} from "./common";

/** Monday-first order of weekday numbers (0 = Sunday). */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const UNAVAILABLE =
  "Scheduling isn't available on this server yet. Once it's switched on, rotas appear here.";

function ScheduleDialog({
  schedule,
  people,
  sites,
  onClose,
  onSaved,
}: {
  schedule: Schedule | null;
  people: Person[];
  sites: Site[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [userId, setUserId] = useState(schedule?.userId ?? "");
  const [siteId, setSiteId] = useState(schedule?.siteId ?? "");
  const [repeat, setRepeat] = useState<"weekly" | "once">(schedule?.date ? "once" : "weekly");
  const [weekday, setWeekday] = useState(String(schedule?.weekday ?? 1));
  const [date, setDate] = useState(schedule?.date ?? thisWeek().today);
  const [startTime, setStart] = useState(schedule?.startTime ?? "09:00");
  const [endTime, setEnd] = useState(schedule?.endTime ?? "11:00");
  const [active, setActive] = useState(schedule?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!userId || !siteId) return setError("Choose a person and a site.");
    if (repeat === "once" && !isIsoDate(date)) return setError("Choose a date.");
    if (!isHhMm(startTime) || !isHhMm(endTime)) return setError("Choose a start and an end time.");
    if (endTime <= startTime) return setError("The end time must be after the start time.");
    const input: ScheduleInput & { active?: boolean } = {
      userId,
      siteId,
      weekday: repeat === "weekly" ? Number(weekday) : null,
      date: repeat === "once" ? date : null,
      startTime,
      endTime,
    };
    setBusy(true);
    try {
      if (schedule) {
        await patch<Schedule>(`/admin/schedules/${schedule.id}`, { ...input, active });
        onSaved("Schedule updated.");
      } else {
        await post<Schedule>("/admin/schedules", input);
        onSaved("Schedule added.");
      }
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  }

  return (
    <Dialog title={schedule ? "Edit schedule" : "Add to the schedule"} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <div className="grid cols-2">
          <Field label="Person">
            <select value={userId} onChange={(e) => setUserId(e.target.value)} required>
              <option value="">Choose…</option>
              {people
                .filter((p) => p.active || p.id === schedule?.userId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Site">
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
              <option value="">Choose…</option>
              {sites
                .filter((s) => s.active || s.id === schedule?.siteId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
        <fieldset className="adm-fieldset">
          <legend>When</legend>
          <div className="stack">
            <label className="adm-check">
              <input
                type="radio"
                name="repeat"
                checked={repeat === "weekly"}
                onChange={() => setRepeat("weekly")}
              />
              <span>Every week on</span>
              <select
                value={weekday}
                onChange={(e) => setWeekday(e.target.value)}
                disabled={repeat !== "weekly"}
                style={{ width: "auto" }}
                aria-label="Weekday"
              >
                {WEEK_ORDER.map((d) => (
                  <option key={d} value={d}>
                    {WEEKDAY_NAMES[d]}
                  </option>
                ))}
              </select>
            </label>
            <label className="adm-check">
              <input
                type="radio"
                name="repeat"
                checked={repeat === "once"}
                onChange={() => setRepeat("once")}
              />
              <span>Just once, on</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={repeat !== "once"}
                required={repeat === "once"}
                style={{ width: "auto" }}
                aria-label="Date"
              />
            </label>
          </div>
        </fieldset>
        <div className="grid cols-2">
          <Field label="Start (UK time)">
            <input type="time" value={startTime} onChange={(e) => setStart(e.target.value)} required />
          </Field>
          <Field label="End (UK time)">
            <input type="time" value={endTime} onChange={(e) => setEnd(e.target.value)} required />
          </Field>
        </div>
        {schedule && (
          <Check label="Active (untick to pause without deleting)" checked={active} onChange={setActive} />
        )}
        {error && <div className="banner danger">{error}</div>}
        <div className="row between">
          <div className="row">
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
          {schedule && (
            <button type="button" className="btn ghost" onClick={() => setDeleting(true)} disabled={busy}>
              Delete
            </button>
          )}
        </div>
      </form>
      {deleting && schedule && (
        <ConfirmDialog
          title="Delete this schedule?"
          confirmLabel="Delete"
          danger
          onConfirm={async () => {
            await del(`/admin/schedules/${schedule.id}`);
            onSaved("Schedule deleted.");
          }}
          onClose={() => setDeleting(false)}
        >
          <p>
            {schedule.userName} at {schedule.siteName}. Past visits aren't affected.
          </p>
        </ConfirmDialog>
      )}
    </Dialog>
  );
}

function SlotButton({ s, onClick, showDate }: { s: Schedule; onClick: () => void; showDate?: boolean }) {
  return (
    <button type="button" className={`adm-slot${s.active ? "" : " inactive"}`} onClick={onClick}>
      <span className="time">
        {showDate && s.date ? `${fmtIsoDate(s.date)} · ` : ""}
        {s.startTime}–{s.endTime}
      </span>
      <br />
      {s.userName}
      <br />
      <span className="muted">{s.siteName}</span>
      {!s.active && <span className="muted"> · paused</span>}
    </button>
  );
}

export function SchedulesPage() {
  const schedules = useApi<Schedule[]>("/admin/schedules");
  const people = usePeople();
  const sites = useSites();
  const [weekOffset, setWeekOffset] = useState(0);
  const monday = addDays(thisWeek().monday, weekOffset * 7);
  const sunday = addDays(monday, 6);
  const occurrences = useApi<ScheduleOccurrence[]>(`/admin/schedule/occurrences?from=${monday}&to=${sunday}`);
  const [editing, setEditing] = useState<Schedule | "new" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const canEdit = !!people.data && !!sites.data && !schedules.error;

  return (
    <div className="page">
      <PageHeader
        title="Schedules"
        sub="Who should be where, so you can see late or missed visits."
        actions={
          <button className="btn primary" onClick={() => setEditing("new")} disabled={!canEdit}>
            Add
          </button>
        }
      />
      {message && <div className="banner ok">{message}</div>}

      <section className="adm-section">
        <h2>Every week</h2>
        <LoadState state={schedules} unavailable={UNAVAILABLE}>
          {(list) => {
            const weekly = list.filter((s) => s.weekday != null);
            const oneOff = list
              .filter((s) => s.date != null && s.date >= thisWeek().today)
              .sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`));
            return (
              <>
                {list.length === 0 && (
                  <p className="muted">Nothing scheduled yet. Press “Add” to set up a regular visit.</p>
                )}
                <div className="adm-week">
                  {WEEK_ORDER.map((d) => {
                    const slots = weekly
                      .filter((s) => s.weekday === d)
                      .sort((a, b) => a.startTime.localeCompare(b.startTime));
                    return (
                      <div className="adm-day" key={d}>
                        <h3>{WEEKDAY_NAMES[d]}</h3>
                        {slots.length ? (
                          slots.map((s) => <SlotButton key={s.id} s={s} onClick={() => setEditing(s)} />)
                        ) : (
                          <span className="muted small">—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <h2 className="adm-section">One-off dates</h2>
                {oneOff.length ? (
                  <div className="adm-week">
                    {oneOff.map((s) => (
                      <SlotButton key={s.id} s={s} showDate onClick={() => setEditing(s)} />
                    ))}
                  </div>
                ) : (
                  <Empty>No upcoming one-off visits.</Empty>
                )}
              </>
            );
          }}
        </LoadState>
      </section>

      <section className="adm-section">
        <div className="row between">
          <h2 style={{ margin: 0 }}>
            {weekOffset === 0 ? "This week" : `Week of ${fmtIsoDate(monday, { weekday: undefined })}`}
          </h2>
          <div className="row">
            <button className="btn sm" onClick={() => setWeekOffset((w) => w - 1)} aria-label="Previous week">
              ← Previous
            </button>
            {weekOffset !== 0 && (
              <button className="btn sm" onClick={() => setWeekOffset(0)}>
                This week
              </button>
            )}
            <button className="btn sm" onClick={() => setWeekOffset((w) => w + 1)} aria-label="Next week">
              Next →
            </button>
          </div>
        </div>
        <p className="muted small">
          {fmtIsoDate(monday)} to {fmtIsoDate(sunday)}
        </p>
        <LoadState state={occurrences} unavailable={UNAVAILABLE}>
          {(list) =>
            list.length ? (
              <div className="table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Time</th>
                      <th>Person</th>
                      <th>Site</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...list]
                      .sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`))
                      .map((o) => (
                        <tr key={`${o.scheduleId}-${o.date}`}>
                          <td className="nowrap">{fmtIsoDate(o.date)}</td>
                          <td className="nowrap mono">
                            {o.startTime}–{o.endTime}
                          </td>
                          <td>{o.userName}</td>
                          <td>{o.siteName}</td>
                          <td>
                            <span className="row" style={{ gap: 6 }}>
                              <OccurrenceBadge status={o.status} />
                              {o.visitId && (
                                <Link to={`/admin/visits/${o.visitId}`} className="small">
                                  Visit
                                </Link>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty>Nothing scheduled this week.</Empty>
            )
          }
        </LoadState>
      </section>

      {editing && people.data && sites.data && (
        <ScheduleDialog
          schedule={editing === "new" ? null : editing}
          people={people.data}
          sites={sites.data}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null);
            setMessage(msg);
            void schedules.reload();
            void occurrences.reload();
          }}
        />
      )}
    </div>
  );
}
